import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import $api from '../api'

export interface User {
  email: string
  password: string
  name: string
  phone: string
  identifier: string
  validatedAt?: number
  bannedAt?: number
  registeredAt: number

  // optional extra fields from backend (if present)
  _id?: string
  id?: string
  city?: string
  businessType?: string
  createdAt?: number
}

interface State {
  token?: string
  tokenExpiresAt?: number
  refreshToken?: string
  refreshTokenExpiresAt?: number
  user?: User
  profileLoading: boolean
}

export interface AuthDTO {
  email: string
  password: string
}

export interface RegisterDTO extends AuthDTO {
  phone: string
  name: string
  city: string
  businessType: string
  identifier: string
}

export interface AuthResponseDTO {
  token: string
  refreshToken?: string
  user?: User
}

interface RegisterResponseDTO {
  auth: AuthResponseDTO
  user: User
}

export type ClaimResetPasswordCodeDTO = Pick<AuthDTO, 'email'> & { code: number }

export const attemptLogin = createAsyncThunk<AuthResponseDTO, AuthDTO>(
  'auth/authorization',
  async (payload) => {
    const response = await $api.post('user/auth', payload)
    return response.data
  },
)

export const attemptRegister = createAsyncThunk<RegisterResponseDTO, RegisterDTO>(
  'auth/registration',
  async (payload) => {
    const response = await $api.post('user/registration', payload)
    return response.data
  },
)

export const attemptSendResetPasswordCode = createAsyncThunk<
  string,
  Pick<AuthDTO, 'email'>
>('auth/forgot-password/send-code', async (payload) => {
  await $api.post('user/forgot-password/send-code', payload)
  return payload.email
})

export const attemptClaimResetPasswordCode = createAsyncThunk<
  AuthResponseDTO,
  ClaimResetPasswordCodeDTO
>('auth/forgot-password/claim-code', async (payload) => {
  const response = await $api.post('user/forgot-password/claim-code', payload)
  return response.data
})

/**
 * Догружаем профиль пользователя.
 * Пробуем несколько endpoint'ов, чтобы не ломаться, если на разных стендах путь отличается.
 */
export const attemptGetMe = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/me',
  async (_, { rejectWithValue }) => {
    const endpoints = ['user/me', 'user/profile', 'user/info']

    let lastError: any = null

    for (const endpoint of endpoints) {
      try {
        const response = await $api.get(endpoint)
        const data = response?.data

        const candidate = data?.user ?? data?.data?.user ?? data?.data ?? data

        if (candidate && typeof candidate === 'object') {
          return candidate as User
        }
      } catch (e: any) {
        lastError = e
      }
    }

    const msg =
      lastError?.response?.data?.message ||
      lastError?.message ||
      'Failed to fetch account info'

    return rejectWithValue(msg)
  },
)

export const initialState: State = {
  profileLoading: false,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: () => {
      // Handled in store.ts in rootReducer
    },
  },
  extraReducers: (builder) => {
    builder.addCase(attemptLogin.fulfilled, (state, { payload }) => {
      state.token = payload.token
      state.refreshToken = payload.refreshToken
      state.user = payload.user
    })

    builder.addCase(attemptRegister.fulfilled, (state, { payload }) => {
      state.token = payload.auth.token
      state.refreshToken = payload.auth.refreshToken
      state.user = payload.user
    })

    builder.addCase(attemptClaimResetPasswordCode.fulfilled, (state, { payload }) => {
      state.token = payload.token
      state.refreshToken = payload.refreshToken
      state.user = payload.user
    })

    builder.addCase(attemptGetMe.pending, (state) => {
      state.profileLoading = true
    })

    builder.addCase(attemptGetMe.fulfilled, (state, { payload }) => {
      state.profileLoading = false
      state.user = payload
    })

    builder.addCase(attemptGetMe.rejected, (state) => {
      state.profileLoading = false
    })
  },
})

export const { logout } = authSlice.actions
export default authSlice.reducer
